"use client";

import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "@/lib/supabaseClient";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function LoginPage() {
  const supabase = useMemo(() => getSupabaseClient(), []);
  const searchParams = useSearchParams();
  const [showLegalNotice, setShowLegalNotice] = useState(false);

  const handleLoginClick = useCallback(() => {
    setShowLegalNotice(true);
  }, []);

  const handleAcceptLegalNotice = useCallback(async () => {
    setShowLegalNotice(false);
    await performLogin();
  }, []);

  const performLogin = useCallback(async () => {
    const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
    const invite = searchParams?.get('invite');
    // Persist invite in localStorage to avoid adding query params to redirect URL
    if (invite) {
      try { localStorage.setItem('invite_code', invite); } catch {}
    }
    // Fallback: if origin is unavailable (SSR safety), default to localhost
    const redirectTo = `${origin || 'http://localhost:3000'}/auth/callback`;

    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo }
    });
  }, [supabase, searchParams]);

  return (
    <>
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-md w-full space-y-6 text-center">
          <h1 className="text-2xl font-semibold">Iniciar sesión</h1>
          <p className="text-muted-foreground">Usa tu cuenta de Google para continuar.</p>
          <Button onClick={handleLoginClick} size="lg">Continuar con Google</Button>
        </div>
      </div>

      <Dialog open={showLegalNotice} onOpenChange={setShowLegalNotice}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aviso Legal y Términos de Uso</DialogTitle>
            <DialogDescription>
              Antes de continuar, debe leer y aceptar nuestros términos legales.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 text-sm text-muted-foreground">
            <div className="bg-muted/50 p-4 rounded-lg">
              <h3 className="font-semibold text-foreground mb-2">Aviso legal de entrada a la plataforma</h3>
              <p className="mb-3 font-semibold">Aviso Legal</p>
              <p className="mb-3">
                Esta plataforma utiliza inteligencia artificial generativa entrenada con bases de conocimiento en psicología social para interpretar la información registrada y ofrecer conceptos orientativos sobre posibles causas de comportamiento.
              </p>
              <p className="mb-3">
                <span className="font-bold">Este contenido no constituye un diagnóstico ni dictamen médico o científico.</span> Si desea una valoración profesional, consulte a un especialista. En este <a href="https://engramapsicologia.es/privacidad/" className="underline text-primary"  target="_blank" rel="noopener noreferrer">enlace</a> encontrará más información.
              </p>
              <p className="mb-3">
                Tenga en cuenta que la IA puede cometer errores o interpretaciones imprecisas.
              </p>
              <p className="mb-3">
                La información introducida no es almacenada por enGrama, permanece únicamente en los dispositivos donde se utiliza la plataforma. Para conocer más detalles consulte nuestra <a href="https://engramapsicologia.es/privacidad/" className="underline text-primary" target="_blank" rel="noopener noreferrer">Política de Privacidad</a> en este enlace.
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowLegalNotice(false)}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleAcceptLegalNotice}
              className="w-full sm:w-auto"
            >
              Acepto los términos y continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}